import * as L from 'leaflet';
import '__common/pixiCanvasFallback';
import { autoDetectRenderer } from 'pixi.js';

let shift;

export class PixiOverlay extends L.Layer {
  constructor(drawCallback, pixiContainer, options = {}) {
    super();
    L.setOptions(this, {
      padding: 0,
      forceCanvas: true,
      resolution: L.Browser.retina ? 2 : 1,
      projectionZoom: (map) => (map.getMaxZoom() + map.getMinZoom()) / 2,
      ...options
    });

    L.stamp(this);
    this._drawCallback = drawCallback;
    this._pixiContainer = pixiContainer;
    this._rendererOptions = {
      backgroundAlpha: 0,
      resolution: this.options.resolution,
      antialias: true,
      // Leaflet overlay defaults to canvas so we do not compete with map WebGL
      // contexts. Requires CanvasRenderer from pixiCanvasFallback.
      forceCanvas: this.options.forceCanvas,
    };
  }

  onAdd(map) {
    this._map = map;

    if (!this._container) {
      const container = this._container = L.DomUtil.create('div', 'leaflet-pixi-overlay');
      container.id = 'roofsSelectorCanvas';
      try {
        this._renderer = autoDetectRenderer(this._rendererOptions);
      } catch (_) {
        this._renderer = autoDetectRenderer({ ...this._rendererOptions, forceCanvas: true });
      }
      container.appendChild(this._renderer.view);

      if (this._zoomAnimated) {
        L.DomUtil.addClass(container, 'leaflet-zoom-animated');
      }
    }

    this.getPane().appendChild(this._container);

    this._initialZoom = this.options.projectionZoom(map);
    this._wgsOrigin = L.latLng([0, 0]);
    this._disableLeafletRounding();
    this._wgsInitialShift = map.project(this._wgsOrigin, this._initialZoom);
    this._enableLeafletRounding();
    this._mapInitialZoom = map.getZoom();
    this._scale = map.getZoomScale(this._mapInitialZoom, this._initialZoom);

    this.utils = {
      latLngToLayerPoint: (latLng, zoom = this._initialZoom) =>
        map.project(L.latLng(latLng), zoom),

      layerPointToLatLng: (point, zoom = this._initialZoom) =>
        map.unproject(L.point(point), zoom),

      getScale: (zoom) =>
        zoom === undefined ? this._scale : map.getZoomScale(zoom, this._initialZoom),

      getRenderer: () => this._renderer,
      getContainer: () => this._pixiContainer,
      getMap: () => this._map,

      movePixiContainer: () =>
        this._pixiContainer.position.set(shift.x, shift.y),

      restorePixiContainer: () =>
        this._pixiContainer.position.set(0, 0)
    };

    this._update();
  }

  onRemove() {
    L.DomUtil.remove(this._container);
  }

  getEvents() {
    const events = {
      zoom: this._onZoom.bind(this),
      moveend: this._update.bind(this),
      zoomend: this._zoomChange.bind(this)
    };

    if (this._zoomAnimated) {
      events.zoomanim = this._onAnimZoom.bind(this);
    }

    return events;
  }

  _onAnimZoom(ev) {
    this._updateTransform(ev.center, ev.zoom);
  }

  _onZoom() {
    this._updateTransform(this._map.getCenter(), this._map.getZoom());
  }

  _updateTransform(center, zoom) {
    const scale = this._map.getZoomScale(zoom, this._zoom);
    const position = L.DomUtil.getPosition(this._container);
    const viewHalf = this._map.getSize().multiplyBy(0.5 + this.options.padding);
    const currentCenterPoint = this._map.project(this._center, zoom);
    const destCenterPoint = this._map.project(center, zoom);
    const centerOffset = destCenterPoint.subtract(currentCenterPoint);
    const topLeftOffset = viewHalf.multiplyBy(-scale).add(position).add(viewHalf).subtract(centerOffset);

    if (L.Browser.any3d) {
      L.DomUtil.setTransform(this._container, topLeftOffset, scale);
    } else {
      L.DomUtil.setPosition(this._container, topLeftOffset);
    }
  }

  _update() {
    if (this._map._animatingZoom && this._bounds) return;

    const p = this.options.padding;
    const mapSize = this._map.getSize();
    const min = this._map.containerPointToLayerPoint(mapSize.multiplyBy(-p)).round();

    this._bounds = new L.Bounds(min, min.add(mapSize.multiplyBy(1 + p * 2)).round());
    this._center = this._map.getCenter();
    this._zoom = this._map.getZoom();

    const view = this._renderer.view;
    const b = this._bounds;
    const container = this._container;
    const size = b.getSize();
    L.DomUtil.setPosition(container, b.min);

    if (!this._renderer.size || this._renderer.size.x !== size.x || this._renderer.size.y !== size.y) {
      const gl = this._renderer.gl || null;
      if (gl) {
        this._renderer.resolution = this.options.resolution;
      }
      this._renderer.resize(size.x, size.y);
      view.style.width = `${size.x}px`;
      view.style.height = `${size.y}px`;

      if (gl && gl.drawingBufferWidth !== this._renderer.width) {
        this._renderer.resolution = this.options.resolution * gl.drawingBufferWidth / this._renderer.width;
        this._renderer.resize(size.x, size.y);
      }

      this._renderer.size = size;
    }

    this._disableLeafletRounding();
    shift = this._map.latLngToLayerPoint(this._wgsOrigin)
      ._subtract(this._wgsInitialShift.multiplyBy(this._scale))
      ._subtract(b.min);

    this._pixiContainer.scale.set(this._scale);
    this._drawCallback(this.utils);
    this._enableLeafletRounding();
  }

  _disableLeafletRounding() {
    this._leaflet_round = L.Point.prototype._round;
    L.Point.prototype._round = function () { return this; };
  }

  _enableLeafletRounding() {
    L.Point.prototype._round = this._leaflet_round;
  }

  _zoomChange() {
    this._scale = this._map.getZoomScale(this._map.getZoom(), this._initialZoom);
  }
}

export function pixiOverlay(drawCallback, pixiContainer, options) {
  return L.Browser.canvas ? new PixiOverlay(drawCallback, pixiContainer, options) : null;
}

window.PixiOverlay = PixiOverlay;
